import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getCatchRegistrationLineApi } from "api/registration";
import { Registration, RegistrationLine } from "interface/registration";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";

interface RegistrationMobileListProps {
	items: Registration[];
	loading: boolean;
	onEdit: (item: Registration) => void;
	onDelete: (item: Registration) => void;
	canDelete: boolean;
	onLoadMore: () => void;
}

const getStatusClassName = (status: number) => {
	if (status === 0) return "landing-note-list-card--status-red";
	return "landing-note-list-card--status-green";
};

const formatWeight = (value?: number | null) => {
	const asNumber = Number(value || 0);
	return `${asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
	})} kg`;
};

const RegistrationMobileList: FC<RegistrationMobileListProps> = ({
	items,
	loading,
	onEdit,
	onDelete,
	canDelete,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const [expandedRegistrations, setExpandedRegistrations] = useState<number[]>([]);
	const [lineMap, setLineMap] = useState<Record<number, RegistrationLine[]>>({});
	const [lineLoadingMap, setLineLoadingMap] = useState<Record<number, boolean>>({});

	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	const toggleLines = (item: Registration) => {
		const statusValue = Number(item.status);
		if (expandedRegistrations.includes(item.registrationId)) {
			setExpandedRegistrations((prev) =>
				prev.filter((id) => id !== item.registrationId),
			);
			return;
		}

		setExpandedRegistrations((prev) => [...prev, item.registrationId]);
		if (lineMap[item.registrationId] || lineLoadingMap[item.registrationId]) {
			return;
		}

		setLineLoadingMap((prev) => ({ ...prev, [item.registrationId]: true }));

		getCatchRegistrationLineApi(item.registrationId, statusValue)
			.then((response) => {
				setLineMap((prev) => ({
					...prev,
					[item.registrationId]: (response?.data || []) as RegistrationLine[],
				}));
			})
			.finally(() => {
				setLineLoadingMap((prev) => ({
					...prev,
					[item.registrationId]: false,
				}));
			});
	};

	return (
		<div className="landing-note-list-mobile" onScroll={handleScroll}>
			{empty && <div className="landing-note-list-mobile-empty">{t("NoRows")}</div>}

			{items.map((item, itemIndex) => {
				const statusValue = Number(item.status);
				const expanded = expandedRegistrations.includes(item.registrationId);
				const lines = lineMap[item.registrationId] || [];
				const linesLoading = lineLoadingMap[item.registrationId] === true;
				const showEdit = statusValue === 0;
				const showDelete = statusValue === 0 && canDelete;
				const itemKey = `${item.registrationId}-${item.status}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`landing-note-list-card ${getStatusClassName(statusValue)}`}
					>
						<div className="landing-note-list-card-header">
							<div className="landing-note-list-card-note">{item.registrationId}</div>
							<div className="landing-note-list-card-vessel">{item.vesselName || "-"}</div>
							<div className="landing-note-list-card-date">
								<GrideDateFormatter
									date={item.registrationDate}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							</div>
						</div>

						<div className="landing-note-list-card-body">
							<div>
								<span>{t("Time")}</span>
								<strong>
									<GrideDateFormatter
										date={item.registrationDate}
										formatType="HH:mm"
										noTd={true}
									/>
								</strong>
							</div>
							<div>
								<span>{t("VesselRegistrationId")}</span>
								<strong>{item.vesselRegistrationNumber || "-"}</strong>
							</div>
							<div>
								<span>{t("FishingGear")}</span>
								<strong>{item.fishingGear || "-"}</strong>
							</div>
							<div>
								<span>{t("Buyer")}</span>
								<strong>{item.buyerName || item.terminalName || "-"}</strong>
							</div>
							<div>
								<span>{t("Lines")}</span>
								<strong>{item.numberOfLines ?? 0}</strong>
							</div>
							<div>
								<span>{t("TotalWeight")}</span>
								<strong>{formatWeight(item.liveWeightKg)}</strong>
							</div>
						</div>

						<div className="landing-note-list-card-actions registration-list-card-actions">
							{showEdit && (
								<button
									type="button"
									className="landing-note-list-icon-btn"
									onClick={() => onEdit(item)}
									title={t("Edit")}
								>
									<img src={`/icon/${ICON_NAMES.Edit}.svg`} alt={t("Edit")} />
								</button>
							)}
							{showDelete && (
								<button
									type="button"
									className="landing-note-list-icon-btn"
									onClick={() => onDelete(item)}
									title={t("DeleteRegistration")}
								>
									<img src={`/icon/${ICON_NAMES.Delete}.svg`} alt={t("DeleteRegistration")} />
								</button>
							)}
							<button
								type="button"
								className="landing-note-list-icon-btn"
								disabled={true}
								title={item.statusText || "Status"}
							>
								<img
									src={`/icon/${statusValue === 1 ? ICON_NAMES.GreenCheckbox : ICON_NAMES.Checkbox}.svg`}
									alt={item.statusText || "Status"}
								/>
							</button>
							<button
								type="button"
								className="landing-note-list-icon-btn"
								onClick={() => toggleLines(item)}
								title={t("Lines")}
							>
								<img
									src={`/icon/${expanded ? ICON_NAMES.ArrowUp : ICON_NAMES.ArrowDown}.svg`}
									alt={t("Lines")}
								/>
							</button>
						</div>

						{expanded && (
							<div className="landing-note-list-lines">
								{linesLoading && (
									<div className="landing-note-list-lines-loading">{t("Loading")}</div>
								)}
								{!linesLoading && lines.length === 0 && (
									<div className="landing-note-list-lines-empty">{t("NoRows")}</div>
								)}
								{!linesLoading &&
									lines.map((line, lineIndex) => (
										<div
											key={`${line.id ?? "noid"}-${line.lineNumber ?? "noline"}-${lineIndex}`}
											className="landing-note-list-line-row"
										>
											<div className="landing-note-list-line-number">#{line.lineNumber}</div>
											<div className="landing-note-list-line-product">{line.product || "-"}</div>
											<div className="landing-note-list-line-weight">
												{formatWeight(line.liveWeightKg)}
											</div>
										</div>
									))}
							</div>
						)}
					</div>
				);
			})}

			{loading && <div className="landing-note-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default RegistrationMobileList;
